import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useTryOnCredits } from "@/context/TryOnCreditsContext";
import { EventPopup } from "@/components/EventPopup";
import PromCountdown from "@/components/PromCountdown";
import { Heart, ShoppingBag, User, Menu, X, Phone, Sparkles, Instagram, Music2, Facebook, Mail, MapPin } from "lucide-react";
import { useState, useEffect } from "react";
import { mpLead } from "@/lib/metaPixel";

const NAV = [
  { label: "Try On", to: "/try-on" },
  { label: "Schools", to: "/schools" },
  { label: "Prom Cup", to: "/prom-cup" },
  { label: "Dresses", to: "/prom-dresses" },
  { label: "Tux", to: "/tuxedos" },
  { label: "Ambassadors", to: "/ambassadors" },
];

const DRESS_FLYOUT = [
  { to: "/prom-dresses", label: "Prom Dresses", sub: "The spring headliners" },
  { to: "/homecoming-dresses", label: "Homecoming Dresses", sub: "Fall's shortest + sweetest" },
  { to: "/graduation-dresses", label: "Graduation Dresses", sub: "White + ivory + celebration" },
];

const PHONE = "(888) 450-6525";
const PHONE_HREF = "tel:+18884506525";

function AnnouncementBar() {
  // Next-Homecoming Saturday counter (late-September WI convention).
  // Rolls to next year automatically once past.
  const hcTarget = (() => {
    const now = new Date();
    const y = now.getUTCFullYear();
    // Anchor: last Saturday of September at 6pm CT — good rough default.
    const anchor = new Date(`${y}-09-26T18:00:00-05:00`);
    return anchor.getTime() > now.getTime() ? anchor : new Date(`${y + 1}-09-26T18:00:00-05:00`);
  })();
  const hcDays = Math.max(0, Math.floor((hcTarget.getTime() - Date.now()) / 86400000));
  const hcYear = hcTarget.getFullYear();

  const messages = [
    { icon: Sparkles, text: "AI TRY-ON IS LIVE · SEE ANY DRESS ON YOU IN 15 SECONDS" },
    { icon: Sparkles, text: `HOMECOMING ${hcYear} · ${hcDays} DAYS · SHOP THE HOCO EDIT →` },
    { icon: Phone, text: `CALL A STYLIST · ${PHONE}` },
    { icon: MapPin, text: "OCONOMOWOC · SERVING 258 WISCONSIN SCHOOLS" },
  ];
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI((v) => (v + 1) % messages.length), 4200); return () => clearInterval(t); }, [messages.length]);
  const M = messages[i];
  return (
    <a href={PHONE_HREF} className="block bg-ink text-cream text-center py-2 hover:bg-rosemetal transition-colors" data-testid="announcement-bar">
      <span className="inline-flex items-center gap-2 font-label text-[10px] tracking-[0.2em]">
        <M.icon className="w-3 h-3" strokeWidth={1.5} />
        {M.text}
      </span>
    </a>
  );
}

function Header() {
  const { user, logout } = useAuth();
  const { cart, openCart } = useCart();
  const { credits, is_vip } = useTryOnCredits();
  const navigate = useNavigate();
  const [mobile, setMobile] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const cartCount = cart.items.reduce((n, i) => n + (i.quantity || 1), 0);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <AnnouncementBar />
      <PromCountdown />
      <header
        data-testid="site-header"
        className={`sticky top-0 z-40 transition-all duration-300 ${
          scrolled
            ? "bg-cream/85 backdrop-blur-xl shadow-[0_1px_0_rgba(183,110,121,0.15),0_8px_30px_rgba(10,10,10,0.05)]"
            : "bg-cream/60 backdrop-blur-md"
        }`}
      >
        {/* subtle rose gold hairline at the bottom */}
        <span className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-rosemetal/40 to-transparent pointer-events-none" />

        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 flex items-center justify-between h-[76px]">
          <button className="md:hidden -ml-1" onClick={() => setMobile((m) => !m)} data-testid="mobile-menu-btn" aria-label="Menu">
            {mobile ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

          {/* Logo — Heather's Boutique wordmark */}
          <Link to="/" data-testid="logo-link" className="flex flex-col items-center md:items-start leading-none group">
            <img
              src="/heathers-wordmark.png"
              alt="Heather's Boutique"
              className="h-9 sm:h-10 w-auto transition-transform duration-500 group-hover:scale-[1.02]"
              style={{ maxWidth: "min(320px, 65vw)" }}
            />
            <span className="font-label text-[8px] text-rosemetal mt-1.5 tracking-[0.28em] hidden md:block">WISCONSIN FORMALWEAR · EST 2018</span>
          </Link>

          {/* Center nav */}
          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((n) => {
              const flyout = n.label === "Dresses" ? DRESS_FLYOUT : n.label === "Tux" ? [
                { to: "/tuxedos", label: "Tuxedos & Suits", sub: "111 looks" },
                { to: "/accessories", label: "Accessories", sub: "Bow ties · pocket squares" },
                { to: "/shoes", label: "Shoes", sub: "Formal · patent · knit" },
              ] : null;
              const key = n.label.toLowerCase().replace(/\s+/g, "-");
              if (flyout) {
                return (
                  <div key={n.label} className="relative group" data-testid={`nav-${key}-wrapper`}>
                    <Link
                      to={n.to}
                      data-testid={`nav-${key}`}
                      className="relative font-label text-[10px] text-ink tracking-[0.18em] px-3 py-2 rounded-full hover:bg-ivory transition-colors inline-flex items-center gap-1"
                    >
                      {n.label.toUpperCase()}
                      <span className="text-ink/40 group-hover:text-rosemetal transition-colors">▾</span>
                      <span className="absolute left-1/2 -translate-x-1/2 -bottom-0.5 h-px w-0 bg-rosemetal group-hover:w-6 transition-all duration-300" />
                    </Link>
                    <div
                      className="invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-200 absolute top-full left-1/2 -translate-x-1/2 pt-2 z-40"
                      data-testid={`nav-${key}-flyout`}
                    >
                      <div className="bg-ivory border border-cream rounded-sm shadow-lg py-2 min-w-[220px]">
                        {flyout.map((t) => (
                          <Link
                            key={t.to}
                            to={t.to}
                            data-testid={`nav-${key}-${t.to.slice(1)}`}
                            className="block px-4 py-2 hover:bg-blush/40 transition-colors"
                          >
                            <div className="font-body text-sm text-ink">{t.label}</div>
                            <div className="font-label text-[9px] tracking-widest text-ink/50 mt-0.5">{t.sub.toUpperCase()}</div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <Link
                  key={n.label}
                  to={n.to}
                  data-testid={`nav-${key}`}
                  className="relative group font-label text-[10px] text-ink tracking-[0.18em] px-3 py-2 rounded-full hover:bg-ivory transition-colors"
                >
                  {n.label.toUpperCase()}
                  <span className="absolute left-1/2 -translate-x-1/2 -bottom-0.5 h-px w-0 bg-rosemetal group-hover:w-6 transition-all duration-300" />
                </Link>
              );
            })}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-4">
            <a href={PHONE_HREF} className="hidden xl:flex items-center gap-1.5 font-label text-[10px] text-ink hover:text-rosemetal tracking-widest" data-testid="header-phone">
              <Phone className="w-3.5 h-3.5" strokeWidth={1.5} /> {PHONE}
            </a>
            {user?.school_verified && (
              <Link to="/portal" data-testid="school-portal-link" className="hidden xl:inline-flex font-label text-[9px] text-rosemetal border border-rosemetal/40 px-2.5 py-1 rounded-full hover:bg-rosemetal hover:text-cream transition-colors">
                My School
              </Link>
            )}
            {user && (
              <Link
                to={user.school_verified ? "/try-on/pricing" : "/try-on"}
                data-testid="header-tryon-credits"
                title={is_vip ? "Unlimited AI try-ons (VIP)" : !user.school_verified ? "Verify your school to unlock 1 free try-on" : `${credits} AI try-on credits · tap to top up`}
                className={`hidden sm:inline-flex items-center gap-1.5 font-label text-[9px] tracking-widest px-2.5 py-1 rounded-full transition-colors ${
                  is_vip
                    ? "bg-champagne text-ink hover:bg-ink hover:text-champagne"
                    : !user.school_verified
                      ? "bg-blush text-rosemetal border border-rosemetal/40 hover:bg-rosemetal hover:text-cream"
                      : credits > 0
                        ? "border border-rosemetal/40 text-rosemetal hover:bg-rosemetal hover:text-cream"
                        : "border border-ink/20 text-ink/60 hover:border-rosemetal hover:text-rosemetal"
                }`}
              >
                <Sparkles className="w-3 h-3" strokeWidth={2} />
                {is_vip ? "VIP · UNLIMITED" : !user.school_verified ? "VERIFY → 1 FREE" : `${credits} TRY-ON${credits === 1 ? "" : "S"}`}
              </Link>
            )}
            <button
              onClick={openCart}
              data-testid="cart-link"
              aria-label="Open cart"
              className="relative hover:text-rosemetal transition-colors"
            >
              <ShoppingBag className="w-[18px] h-[18px]" strokeWidth={1.5} />
              {cartCount > 0 && (
                <span data-testid="cart-count-badge" className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 bg-rosemetal text-cream rounded-full font-label text-[9px] flex items-center justify-center leading-none">
                  {cartCount}
                </span>
              )}
            </button>
            {user ? (
              <div className="flex items-center gap-3">
                <Link to="/account" data-testid="account-link" title={`${user.name?.split(" ")[0] || "Account"} · your looks & orders`} className="hover:text-rosemetal transition-colors"><User className="w-[18px] h-[18px]" strokeWidth={1.5} /></Link>
                {user.role === "admin" && (
                  <Link to="/admin" data-testid="admin-link" className="font-label text-[9px] text-ink hover:text-rosemetal">Admin</Link>
                )}
                <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn" className="font-label text-[9px] text-muted-foreground hover:text-ink hidden lg:block">Sign out</button>
              </div>
            ) : (
              <Link to="/auth" data-testid="signin-link" className="font-label text-[10px] bg-ink text-cream px-4 py-2 rounded-full hover:bg-rosemetal transition-colors tracking-widest">Sign In</Link>
            )}
          </div>
        </div>

        {mobile && (
          <nav className="md:hidden bg-cream border-t border-rosemetal/20 px-5 py-5 flex flex-col gap-3">
            {NAV.map((n) => (
              n.label === "Tux" ? (
                <div key={n.label} className="border-l-2 border-rosemetal/40 pl-3 space-y-2 py-1">
                  <Link to="/tuxedos" onClick={() => setMobile(false)} className="block font-label text-xs tracking-widest">TUXEDOS & SUITS</Link>
                  <Link to="/accessories" onClick={() => setMobile(false)} className="block font-label text-[10px] tracking-widest text-ink/70">— ACCESSORIES</Link>
                  <Link to="/shoes" onClick={() => setMobile(false)} className="block font-label text-[10px] tracking-widest text-ink/70">— SHOES</Link>
                </div>
              ) : (
                <Link key={n.label} to={n.to} onClick={() => setMobile(false)} className="font-label text-xs tracking-widest py-1">{n.label.toUpperCase()}</Link>
              )
            ))}
            <a href={PHONE_HREF} className="mt-4 pt-4 border-t border-rosemetal/20 font-label text-xs tracking-widest text-rosemetal flex items-center gap-2"><Phone className="w-3.5 h-3.5" /> {PHONE}</a>
          </nav>
        )}
      </header>
    </>
  );
}

function VipListForm() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vip/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), source: "footer" }),
      });
      const j = await r.json();
      if (r.ok && j.ok) {
        setStatus("success");
        setMessage(j.already_subscribed ? "You're already on the list. See you at the next drop." : "Welcome in. Check your inbox for the VIP perks.");
        try { window.posthog?.capture("vip_list_signup", { source: "footer", already_subscribed: !!j.already_subscribed }); } catch { /* noop */ }
        // Meta Pixel — Lead
        if (!j.already_subscribed) {
          try { mpLead({ source: "vip_list_footer" }); } catch { /* noop */ }
        }
      } else {
        setStatus("error");
        setMessage(j.detail || "Something's off. Try again in a minute?");
      }
    } catch {
      setStatus("error");
      setMessage("Couldn't reach us. Try again in a minute?");
    }
  };

  if (status === "success") {
    return (
      <div className="mt-5" data-testid="vip-success">
        <div className="inline-flex items-center gap-2 bg-champagne text-ink px-4 py-2.5 rounded-full font-label text-[10px] tracking-widest">
          <Sparkles className="w-3.5 h-3.5" /> YOU'RE IN
        </div>
        <p className="font-body text-sm text-cream/80 mt-3 leading-relaxed">{message}</p>
      </div>
    );
  }

  return (
    <>
      <form className="mt-5 flex gap-2" onSubmit={submit}>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          data-testid="footer-newsletter-email"
          disabled={status === "loading"}
          className="flex-1 bg-cream/10 border border-cream/20 rounded-full px-4 py-2.5 font-body text-sm text-cream placeholder:text-cream/40 outline-none focus:border-champagne transition-colors disabled:opacity-60"
        />
        <button
          type="submit"
          data-testid="footer-newsletter-submit"
          disabled={status === "loading"}
          className="bg-champagne text-ink px-5 py-2.5 rounded-full font-label text-[10px] tracking-widest hover:bg-ivory transition-colors disabled:opacity-60"
        >
          {status === "loading" ? "…" : "JOIN"}
        </button>
      </form>
      {status === "error" ? (
        <p className="font-label text-[9px] text-rosemetal mt-3 tracking-widest">{message}</p>
      ) : (
        <p className="font-label text-[9px] text-cream/40 mt-3 tracking-widest">NO SPAM · UNSUBSCRIBE ANYTIME</p>
      )}
    </>
  );
}

function Footer() {
  return (
    <footer className="relative mt-32 bg-ink text-cream overflow-hidden" data-testid="site-footer">
      {/* Decorative flourishes */}
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-champagne to-transparent" />
      <div className="absolute top-1/2 -translate-y-1/2 -left-32 w-96 h-96 rounded-full bg-rosemetal/10 blur-3xl" aria-hidden />
      <div className="absolute -bottom-40 -right-32 w-[500px] h-[500px] rounded-full bg-champagne/10 blur-3xl" aria-hidden />

      <div className="relative max-w-[1400px] mx-auto px-6 sm:px-10 pt-20 pb-10">
        {/* Top: brand + newsletter */}
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-12 pb-14 border-b border-cream/15">
          <div>
            <p className="font-label text-[10px] text-champagne tracking-[0.3em] mb-3">SINCE 2018 · OCONOMOWOC, WI</p>
            <div className="flex items-start gap-5">
              {/* H monogram — footer accent, subtle hover glow */}
              <Link
                to="/"
                aria-label="Heather's Boutique home"
                data-testid="footer-h-mark"
                className="group relative inline-flex items-center justify-center w-16 h-16 rounded-full bg-cream/95 shadow-[0_20px_50px_-15px_rgba(247,231,206,0.35)] transition-transform duration-500 hover:scale-105 shrink-0 mt-1"
              >
                <span className="absolute inset-0 rounded-full bg-gradient-to-br from-champagne/0 via-champagne/25 to-rosemetal/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <img
                  src="/heathers-h.png"
                  alt=""
                  aria-hidden
                  className="relative w-10 h-10 object-contain transition-transform duration-500 group-hover:rotate-[8deg]"
                />
              </Link>
              <h3 className="font-display text-6xl leading-[0.9]">
                Heather's<br />
                <span className="italic text-champagne">Boutique</span>
              </h3>
            </div>
            <p className="font-body text-sm text-cream/70 mt-6 max-w-md leading-relaxed">
              Wisconsin's home for prom, homecoming, graduation, and every unforgettable moment. Editorial formalwear, school-exclusive dress locks, AI try-on, and a stylist in every student's pocket.
            </p>
            <div
              className="mt-8 flex flex-wrap gap-6 text-sm"
              itemScope
              itemType="https://schema.org/ClothingStore"
              data-testid="footer-nap-primary"
            >
              <meta itemProp="name" content="Heather's Boutique" />
              {/* Address — the "A" in NAP */}
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=W359N5002+Brown+Street+Oconomowoc+WI+53066"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 text-cream hover:text-champagne transition-colors"
                data-testid="footer-address"
                itemProp="address"
                itemScope
                itemType="https://schema.org/PostalAddress"
              >
                <span className="w-9 h-9 rounded-full border border-cream/25 flex items-center justify-center group-hover:border-champagne group-hover:bg-champagne group-hover:text-ink transition-colors">
                  <MapPin className="w-4 h-4" strokeWidth={1.5} />
                </span>
                <span className="flex flex-col leading-tight not-italic">
                  <span className="font-label text-[9px] text-cream/50 tracking-widest">VISIT THE BOUTIQUE</span>
                  <span className="font-display text-lg">
                    <span itemProp="streetAddress">W359N5002 Brown St, Suite 211</span>
                  </span>
                  <span className="font-body text-[11px] text-cream/50 mt-0.5">
                    <span itemProp="addressLocality">Oconomowoc</span>, <span itemProp="addressRegion">WI</span> <span itemProp="postalCode">53066</span>
                  </span>
                </span>
              </a>
              <a href="tel:+18884506525" className="group inline-flex items-center gap-2 text-cream hover:text-champagne transition-colors" data-testid="footer-phone" itemProp="telephone" content="+1-888-450-6525">
                <span className="w-9 h-9 rounded-full border border-cream/25 flex items-center justify-center group-hover:border-champagne group-hover:bg-champagne group-hover:text-ink transition-colors">
                  <Phone className="w-4 h-4" strokeWidth={1.5} />
                </span>
                <span className="flex flex-col leading-tight">
                  <span className="font-label text-[9px] text-cream/50 tracking-widest">CALL A STYLIST</span>
                  <span className="font-display text-lg">{PHONE}</span>
                </span>
              </a>
              <a href="mailto:info@heathersboutique.com" className="group inline-flex items-center gap-2 text-cream hover:text-champagne transition-colors" itemProp="email" content="info@heathersboutique.com">
                <span className="w-9 h-9 rounded-full border border-cream/25 flex items-center justify-center group-hover:border-champagne group-hover:bg-champagne group-hover:text-ink transition-colors">
                  <Mail className="w-4 h-4" strokeWidth={1.5} />
                </span>
                <span className="flex flex-col leading-tight">
                  <span className="font-label text-[9px] text-cream/50 tracking-widest">EMAIL</span>
                  <span className="font-display text-lg">info@heathersboutique.com</span>
                </span>
              </a>
            </div>
          </div>

          {/* Newsletter */}
          <div className="bg-cream/5 backdrop-blur border border-cream/10 rounded-sm p-8">
            <p className="font-label text-[10px] text-champagne tracking-[0.3em]">◉ VIP LIST</p>
            <h4 className="font-display text-2xl mt-3 leading-tight">Get first pick on new arrivals</h4>
            <p className="font-body text-xs text-cream/60 mt-2 leading-relaxed">Editorial drops, school-early access, and a stylist's cheat sheet — straight to your inbox.</p>
            <VipListForm />
          </div>
        </div>

        {/* Link columns */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-10 py-14">
          <div>
            <div className="font-label text-[10px] text-champagne tracking-widest mb-5">SHOP</div>
            <ul className="flex flex-col gap-3 font-body text-sm text-cream/70">
              <li><Link to="/prom-dresses" className="hover:text-champagne transition-colors">Prom Dresses</Link></li>
              <li><Link to="/homecoming-dresses" className="hover:text-champagne transition-colors">Homecoming</Link></li>
              <li><Link to="/tuxedos" className="hover:text-champagne transition-colors">Tuxedos & Suits</Link></li>
              <li><Link to="/accessories" className="hover:text-champagne transition-colors">Accessories</Link></li>
              <li><Link to="/shoes" className="hover:text-champagne transition-colors">Shoes</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-label text-[10px] text-champagne tracking-widest mb-5">EXPERIENCE</div>
            <ul className="flex flex-col gap-3 font-body text-sm text-cream/70">
              <li><Link to="/try-on" className="hover:text-champagne transition-colors">AI Try-On</Link></li>
              <li><Link to="/compare" className="hover:text-champagne transition-colors">Compare Looks</Link></li>
              <li><Link to="/squads" className="hover:text-champagne transition-colors">Squad Planner</Link></li>
              <li><Link to="/prom-cup" className="hover:text-champagne transition-colors">Prom Cup</Link></li>
              <li><Link to="/ambassadors" className="hover:text-champagne transition-colors">Ambassadors</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-label text-[10px] text-champagne tracking-widest mb-5">SCHOOLS</div>
            <ul className="flex flex-col gap-3 font-body text-sm text-cream/70">
              <li><Link to="/schools" className="hover:text-champagne transition-colors">School Directory</Link></li>
              <li><Link to="/lookbook" className="hover:text-champagne transition-colors">Community Lookbook</Link></li>
              <li><Link to="/memories" className="hover:text-champagne transition-colors">Prom Memories</Link></li>
              <li><Link to="/boosters" className="hover:text-champagne transition-colors">Booster Programs</Link></li>
              <li><Link to="/parents" className="hover:text-champagne transition-colors">For Parents</Link></li>
            </ul>
          </div>
          <div itemScope itemType="https://schema.org/ClothingStore" data-testid="footer-nap">
            <div className="font-label text-[10px] text-champagne tracking-widest mb-5">VISIT</div>
            <meta itemProp="name" content="Heather's Boutique" />
            <meta itemProp="telephone" content="+1-888-450-6525" />
            <meta itemProp="email" content="info@heathersboutique.com" />
            <address className="not-italic font-body text-sm text-cream/70 leading-relaxed" itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
              <span itemProp="streetAddress">W359N5002 Brown Street, Suite 211</span><br />
              <span itemProp="addressLocality">Oconomowoc</span>, <span itemProp="addressRegion">WI</span> <span itemProp="postalCode">53066</span>
            </address>
            <div className="mt-4 font-body text-xs text-cream/50">
              <div>Mon–Sat · 11am — 4:30pm</div>
              <div>Sunday · Closed</div>
            </div>
            <a href="https://www.google.com/maps/dir/?api=1&destination=W359N5002+Brown+Street+Oconomowoc+WI+53066" target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-1.5 font-label text-[10px] text-champagne hover:text-cream tracking-widest">
              <MapPin className="w-3 h-3" /> GET DIRECTIONS
            </a>
          </div>
          <div>
            <div className="font-label text-[10px] text-champagne tracking-widest mb-5">HELP & POLICIES</div>
            <ul className="flex flex-col gap-3 font-body text-sm text-cream/70">
              <li><Link to="/contact" className="hover:text-champagne transition-colors" data-testid="footer-contact-link">Contact Us</Link></li>
              <li><Link to="/support" className="hover:text-champagne transition-colors" data-testid="footer-support-link">Support</Link></li>
              <li><Link to="/shipping" className="hover:text-champagne transition-colors" data-testid="footer-shipping-link">Shipping</Link></li>
              <li><Link to="/returns" className="hover:text-champagne transition-colors" data-testid="footer-returns-link">Returns & Refunds</Link></li>
              <li><Link to="/privacy" className="hover:text-champagne transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-champagne transition-colors" data-testid="footer-terms-link">Terms of Service</Link></li>
              <li><Link to="/accessibility" className="hover:text-champagne transition-colors" data-testid="footer-accessibility-link">Accessibility</Link></li>
            </ul>
          </div>
        </div>

        {/* Social + Signature */}
        <div className="flex flex-wrap items-center justify-between gap-6 py-8 border-t border-cream/15">
          <div className="flex items-center gap-3">
            <a href="https://instagram.com/_heathersboutique" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-10 h-10 rounded-full border border-cream/20 flex items-center justify-center hover:bg-champagne hover:border-champagne hover:text-ink transition-colors" data-testid="social-instagram">
              <Instagram className="w-4 h-4" strokeWidth={1.5} />
            </a>
            <a href="https://tiktok.com/@heathersboutique" target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="w-10 h-10 rounded-full border border-cream/20 flex items-center justify-center hover:bg-champagne hover:border-champagne hover:text-ink transition-colors" data-testid="social-tiktok">
              <Music2 className="w-4 h-4" strokeWidth={1.5} />
            </a>
            <a href="https://facebook.com/heatherssboutique" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-10 h-10 rounded-full border border-cream/20 flex items-center justify-center hover:bg-champagne hover:border-champagne hover:text-ink transition-colors" data-testid="social-facebook">
              <Facebook className="w-4 h-4" strokeWidth={1.5} />
            </a>
          </div>
          <p className="font-display italic text-cream/60 text-lg text-center">
            Made in Wisconsin with love. <Sparkles className="inline w-4 h-4 text-champagne" strokeWidth={1.5} />
          </p>
        </div>

        {/* Legal */}
        <div className="flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-cream/10 font-label text-[9px] text-cream/40 tracking-widest">
          <div>© 2026 HEATHER'S BOUTIQUE · WISCONSIN-FIRST · EVERYWHERE NEXT</div>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            <Link to="/contact" className="hover:text-champagne">CONTACT</Link>
            <Link to="/privacy" className="hover:text-champagne">PRIVACY</Link>
            <Link to="/terms" className="hover:text-champagne">TERMS</Link>
            <Link to="/returns" className="hover:text-champagne">RETURNS</Link>
            <Link to="/shipping" className="hover:text-champagne">SHIPPING</Link>
            <Link to="/accessibility" className="hover:text-champagne">ACCESSIBILITY</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export function Layout({ children }) {
  const location = useLocation();
  const hideChrome = location.pathname === "/auth";
  return (
    <div className="App">
      {!hideChrome && <Header />}
      <main>{children}</main>
      {!hideChrome && <Footer />}
      {!hideChrome && <EventPopup />}
    </div>
  );
}
